import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import FaqAccordion from '@/components/base/FaqAccordion';
import SectionHeading from '@/components/base/SectionHeading';
import { contactInfo, generalFaq, siteMeta, testimonials } from '@/mocks/site';

const processSteps = [
  { icon: 'ri-inbox-archive-line', title: '收集公开信息', desc: '以公开报道、官方声明、可核实的时间节点为主要来源，先归类再动笔。' },
  { icon: 'ri-git-branch-line', title: '梳理人物关系', desc: '把出现的人物列成档案，标注他们之间的关系类型，能确认的才连线。' },
  { icon: 'ri-git-commit-line', title: '排布矛盾链条', desc: '按日期把事件排成节点，标明哪些是引子、哪些是回应，避免因果倒置。' },
  { icon: 'ri-refresh-line', title: '持续更新校准', desc: '当事方有新回应或信息被证伪时及时更新，并在事件详情标注修改时间。' },
];

const principles = [
  { title: '只做梳理，不站队', desc: '我们把信息整理清楚，但不替读者判断谁对谁错，也不使用煽动性标题。' },
  { title: '区分事实与传闻', desc: '已确认的信息与仍在讨论中的说法分开标注，不把猜测写成结论。' },
  { title: '回应如实记录', desc: '收到的公开回应原样呈现，未回应的写明"未回应"，不代为补白。' },
  { title: '来源可追溯', desc: '每条回应都注明来源与时间，方便读者自行核对原始信息。' },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们',
        url: `${siteMeta.domain}/about`,
        description: siteMeta.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '911吃瓜',
        url: siteMeta.domain,
        slogan: siteMeta.slogan,
        email: contactInfo.email,
        description: siteMeta.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteMeta.domain },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteMeta.domain}/about` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: generalFaq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
    []
  );

  return (
    <PageLayout>
      <SeoHead
        title="关于我们 | 内容原则与联系方式 · 911吃瓜"
        description="了解 911吃瓜 的品牌定位、内容原则与整理流程：我们收集公开信息，梳理人物关系图与矛盾链条，如实记录当事人回应，欢迎读者提供线索与更正。"
        keywords="关于911吃瓜,内容原则,信息来源,联系方式,关系梳理"
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="w-full px-4 md:px-8 pt-24 md:pt-28">
        <Breadcrumbs items={[{ label: '关于我们' }]} />
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-primary-600 uppercase">
              <span className="w-5 h-[2px] bg-primary-500 rounded-full" />
              关于 911吃瓜
            </span>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-snug">
              把关系讲清楚，是我们在做的事
            </h1>
            <p className="mt-5 text-sm md:text-base leading-8 text-foreground-600">{siteMeta.intro}</p>
            <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
              在信息碎片化的环境里，一件事常常被拆成无数条零散动态。我们想做的，是把它们重新拼回完整的脉络：谁和谁有关、事情从哪开始、各方又是怎么回应的。
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/cases"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-fire-line" />
                开始浏览事件
              </Link>
              <a
                href={`mailto:${contactInfo.email}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-foreground-300 text-foreground-800 font-semibold hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                联系我们
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20of%20a%20network%20of%20connected%20circles%20and%20flowing%20lines%2C%20warm%20coral%20amber%20and%20green%20palette%20on%20cream%20background%2C%20soft%20gradient%20light%2C%20minimal%20modern%20composition%2C%20high%20detail&width=1000&height=900&seq=about-brand-illustration&orientation=squarish"
              alt="911吃瓜 品牌关系网络插画"
              title="911吃瓜 - 内容梳理理念"
              className="w-full h-72 md:h-[420px] object-cover object-top rounded-lg"
            />
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <SectionHeading
          eyebrow="网站定位"
          title="我们提供什么"
          desc="围绕同一个话题，我们提供三种互补的阅读方式，帮助你从不同角度看清一件事。"
        />
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: 'ri-git-branch-line', title: '人物关系图', desc: '把当事人之间的关系画成网络，谁与谁是直接关系、谁只算同场关联，一眼分辨。', tone: 'bg-primary-100 text-primary-700' },
            { icon: 'ri-git-commit-line', title: '矛盾链条', desc: '按时间排列事件节点，标出引子与回应，看矛盾是如何一步步被推高的。', tone: 'bg-accent-100 text-accent-800' },
            { icon: 'ri-chat-quote-line', title: '当事人回应', desc: '如实收录各方公开表态，标注立场与来源，未回应的也明确写出。', tone: 'bg-secondary-100 text-secondary-900' },
          ].map((card) => (
            <article key={card.title} className="bg-background-50 border border-background-200 rounded-lg p-6">
              <span className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${card.tone}`}>
                <i className={card.icon} />
              </span>
              <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">{card.title}</h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600">{card.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="内容是怎么做出来的"
            title="从来源到成稿的四步"
            desc="每一步都尽量留下可核对的痕迹，让整理过程本身也经得起推敲。"
          />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((step, index) => (
              <article key={step.title} className="relative bg-background-50 border border-background-200 rounded-lg p-6">
                <span className="absolute top-5 right-5 font-heading font-black text-3xl text-background-300">
                  0{index + 1}
                </span>
                <span className="w-11 h-11 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-xl">
                  <i className={step.icon} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{step.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <SectionHeading
              eyebrow="内容原则"
              title="我们坚持的几条底线"
              desc="这些原则决定了我们会写什么、不写什么，也决定了呈现方式。"
            />
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {principles.map((p) => (
              <article key={p.title} className="bg-background-100 border border-background-200 rounded-lg p-5">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-accent-500 text-primary-950 flex items-center justify-center text-sm">
                    <i className="ri-check-line" />
                  </span>
                  <h3 className="font-heading font-bold text-foreground-950">{p.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-7 text-foreground-600">{p.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="用户口碑" title="读者怎么说" align="center" />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((item, index) => (
              <figure
                key={item.name}
                className={`rounded-lg p-5 border ${
                  index % 2 === 0 ? 'bg-background-50 border-background-200' : 'bg-secondary-50 border-secondary-200'
                }`}
              >
                <div className="flex items-center gap-1 text-secondary-600">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <i key={s} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-7 text-foreground-700">“{item.content}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-heading font-bold">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-950">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.tag}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="联系方式"
              title="有线索或发现错误，欢迎告诉我们"
              desc="我们欢迎读者提供可靠来源的线索，也欢迎针对疏漏提出更正。核实后我们会尽快更新内容。"
            />
            <div className="mt-7 space-y-4">
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-start gap-3 bg-background-50 border border-background-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
              >
                <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-xl shrink-0">
                  <i className="ri-mail-send-line" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-950">内容线索与更正</span>
                  <span className="block text-sm text-primary-600 break-all">{contactInfo.email}</span>
                </span>
              </a>
              <a
                href={`mailto:${contactInfo.press}`}
                className="flex items-start gap-3 bg-background-50 border border-background-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
              >
                <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center text-xl shrink-0">
                  <i className="ri-newspaper-line" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-950">媒体与合作</span>
                  <span className="block text-sm text-primary-600 break-all">{contactInfo.press}</span>
                </span>
              </a>
              <div className="flex items-start gap-3 bg-background-50 border border-background-200 rounded-lg p-4">
                <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center text-xl shrink-0">
                  <i className="ri-time-line" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-950">更新时段</span>
                  <span className="block text-sm text-foreground-600">{contactInfo.hours}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950">常见问题</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              关于内容来源、关系图依据与更新机制，这里有一些常见回答。
            </p>
            <div className="mt-5">
              <FaqAccordion items={generalFaq} />
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}